// ── Adapter Supabase do IConfigRepository ─────────────────────────
// Implementa a porta do módulo Config usando o cliente Supabase. É a ÚNICA
// camada que conhece a tabela `config` e a leitura de `leads`.

import { getSupabase } from '@infra/supabase/client';
import type { Json } from '@/types/database.types';
import type { IConfigRepository, OriginCountField } from '@modules/config';
import { ENTERPRISE_GOALS_CONFIG_KEY } from '@config/enterprise';

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase não inicializado.');
  return s;
};

export class SupabaseConfigRepository implements IConfigRepository {
  getConfigValue = async <T = Json>(workspaceId: string, key: string): Promise<T | null> => {
    const { data, error } = await client()
      .from('config')
      .select('value')
      .eq('workspace_id', workspaceId)
      .eq('key', key)
      .maybeSingle();
    if (error) throw error;
    return (data?.value as T) ?? null;
  };

  setConfigValue = async (workspaceId: string, key: string, value: Json): Promise<void> => {
    if (key === ENTERPRISE_GOALS_CONFIG_KEY) {
      const { data: { user }, error: userError } = await client().auth.getUser();
      if (userError) throw userError;
      if (!user?.id) throw new Error('Usuário não autenticado.');

      const { data: role, error: roleError } = await client()
        .rpc('current_user_team_role', { p_workspace_id: workspaceId });
      if (roleError) throw roleError;
      const { data: owner, error: ownerError } = await client().rpc('current_user_is_owner');
      if (ownerError) throw ownerError;
      if (role !== 'gestor' && owner !== true) {
        throw new Error('Apenas gestor pode editar metas Enterprise.');
      }
    }

    const { error } = await client()
      .from('config')
      .upsert(
        { workspace_id: workspaceId, key, value },
        { onConflict: 'workspace_id,key' },
      );
    if (error) throw error;
  };

  countLeadsByField = async (workspaceId: string, field: OriginCountField): Promise<Record<string, number>> => {
    const { data, error } = await client()
      .from('leads')
      .select(field)
      .eq('workspace_id', workspaceId)
      .not(field, 'is', null);

    if (error) throw error;

    const counts: Record<string, number> = {};
    for (const row of data || []) {
      const key = String((row as Record<OriginCountField, string | null>)[field] || '').trim();
      if (!key) continue;
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  };
}
